import { useState, type CSSProperties } from 'react';
import type { JustPulledItem } from '../api';
import { CardModal, ChevronRight } from '../design-system';
import { JustPulledChip } from './JustPulledChip';
import { JustPulledSkeleton } from './JustPulledSkeleton';

export interface JustPulledProps {
  items: JustPulledItem[];
  loading?: boolean;
  /** Compact mobile variant — no header, smaller chips. */
  compact?: boolean;
  /**
   * Desktop chevron click. Hidden when omitted or in compact mode.
   * In the iframe this opens the in-iframe `?view=just-pulled` page.
   */
  onShowAll?: () => void;
}

// Design Constructor surface: `JustPulled`. Background, border, font and
// chevron colours all flow through `--co-JustPulled-*` vars with defaults
// matching the previous hardcoded glass wash, so unmodified deployments
// stay pixel-identical.
const PANEL_BG =
  'linear-gradient(' +
  'var(--co-JustPulled-bgDirection, to bottom), ' +
  'color-mix(in srgb, var(--co-JustPulled-bgFrom, rgba(255,255,255,0.20)) ' +
  'calc(var(--co-JustPulled-bgOpacity, 1) * 100%), transparent) 0%, ' +
  'color-mix(in srgb, var(--co-JustPulled-bgTo, rgba(153,153,153,0.20)) ' +
  'calc(var(--co-JustPulled-bgOpacity, 1) * 100%), transparent) 100%)';
const CHEVRON_BG =
  'linear-gradient(180deg, var(--co-JustPulled-chevronBg, rgba(255,255,255,0.20)) 0%, var(--co-JustPulled-chevronBg, rgba(255,255,255,0.20)) 100%)';

/**
 * Live "Just Pulled" activity strip. Pure presentational — fetching is
 * the caller's responsibility (typically `useJustPulled` in PackPage).
 *
 * Marquee:
 *   - Items are duplicated; the duplicated row translates by 50% so the
 *     loop seam is invisible.
 *   - Animation duration scales with item count (5s per item, capped).
 *   - Hovering the marquee pauses the animation.
 *   - Edge fade hides the snap at the strip's left/right boundary.
 */
export function JustPulled({
  items,
  loading = false,
  compact = false,
  onShowAll,
}: JustPulledProps) {
  const [selectedItem, setSelectedItem] = useState<JustPulledItem | null>(null);

  // Hide the section entirely when there's nothing to show and we're
  // not still waiting on the first fetch.
  if (!loading && items.length === 0) return null;

  const showHeader = !compact;
  const showChevron = !compact && typeof onShowAll === 'function';

  // Compact (mobile, Figma 226:2001): single-row panel — inline "JUST
  // PULLED" label on the left, marquee on the right. Mask has a fully-
  // transparent plateau under the label, then a short fade-in, so chips
  // are completely invisible behind the label (no ghosting).
  //
  // The whole section is tap-to-open: anywhere on the mobile strip
  // navigates to the full Just Pulled page (no separate chevron — the
  // strip is small and the entire surface is the affordance).
  if (compact) {
    const LABEL_BAND = 92; // px fully hidden on the left for the label
    const FADE = 16; // px of chip fade-in just past the label
    const MASK = `linear-gradient(to right, transparent 0, transparent ${LABEL_BAND}px, #000 ${LABEL_BAND + FADE}px, #000 calc(100% - 24px), transparent 100%)`;

    const tappable = typeof onShowAll === 'function';
    const tapHandlers = tappable
      ? {
          role: 'button' as const,
          tabIndex: 0,
          onClick: () => onShowAll!(),
          onKeyDown: (e: import('react').KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onShowAll!();
            }
          },
        }
      : {};

    return (
      <section
        className={
          'group relative w-full overflow-hidden text-white h-[46px] ' +
          (tappable ? 'cursor-pointer' : '')
        }
        aria-label={tappable ? 'See all recent pulls' : 'Just Pulled — recent pulls'}
        {...tapHandlers}
      >
        <p
          className="
            absolute left-2.25 top-1/2 -translate-y-1/2 z-[2]
            text-[13px] font-bold uppercase whitespace-nowrap text-white
            tracking-[-0.3px] leading-none
          "
        >
          Just Pulled
        </p>
        <div
          className="absolute inset-y-[6px] left-0 right-[6px] overflow-hidden"
          style={{ maskImage: MASK, WebkitMaskImage: MASK }}
        >
          {loading && items.length === 0 ? (
            <div className="pl-[6px]">
              <JustPulledSkeleton compact />
            </div>
          ) : (
            <Marquee items={items} compact onItemClick={setSelectedItem} />
          )}
        </div>

        <CardModal
          open={selectedItem !== null}
          onClose={() => setSelectedItem(null)}
          item={selectedItem}
          label="Just Pulled"
        />
      </section>
    );
  }

  // Desktop: full glass panel with header + chevron next to the label.
  return (
    <section
      data-co-component="JustPulled"
      className="
        group relative w-full overflow-hidden text-white
        [border-radius:var(--co-JustPulled-borderRadius,28px)] shadow-[0_64px_34px_0_rgba(0,0,0,0.05)]
        p-[15px]
      "
      style={{
        backgroundImage: PANEL_BG,
        backdropFilter: 'blur(var(--co-JustPulled-glassBlur, 27px))',
        WebkitBackdropFilter: 'blur(var(--co-JustPulled-glassBlur, 27px))',
        borderStyle: 'solid',
        borderWidth: 'var(--co-JustPulled-borderWidth, 1px)',
        borderColor: 'var(--co-JustPulled-borderColor, rgba(171,174,179,0.24))',
        fontFamily: 'var(--co-JustPulled-fontFamily), Inter, system-ui, sans-serif',
      }}
      aria-label="Just Pulled — recent pulls"
    >
      {showHeader && (
        <header className="mb-3 flex items-center gap-2">
          <h2
            className="font-bold uppercase leading-[26px]"
            style={{
              color: 'var(--co-JustPulled-labelColor, #ffffff)',
              fontSize: 'var(--co-JustPulled-labelSize, 18px)',
            }}
          >
            Just Pulled
          </h2>
          {showChevron && (
            <button
              type="button"
              onClick={onShowAll}
              aria-label="See all recent pulls"
              className="
                flex size-[26px] items-center justify-center
                rounded-full border border-[rgba(171,174,179,0.24)]
                backdrop-blur-[27px]
                transition-transform active:scale-95 hover:opacity-90
              "
              style={{
                backgroundImage: CHEVRON_BG,
                color: 'var(--co-JustPulled-chevronIcon, #ffffff)',
              }}
            >
              <ChevronRight size={14} />
            </button>
          )}
        </header>
      )}

      {loading && items.length === 0 ? (
        <JustPulledSkeleton />
      ) : (
        <Marquee items={items} compact={false} onItemClick={setSelectedItem} />
      )}

      <CardModal
        open={selectedItem !== null}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
        label="Just Pulled"
      />
    </section>
  );
}

function Marquee({
  items,
  compact,
  onItemClick,
}: {
  items: JustPulledItem[];
  compact: boolean;
  onItemClick?: (item: JustPulledItem) => void;
}) {
  // 10s per chip — slow enough to comfortably read each card title at
  // typical strip widths. Clamped so very small / very large item counts
  // still feel right.
  const durationSeconds = Math.min(240, Math.max(40, items.length * 10));

  // Use the `.just-pulled-marquee` class from index.css — it wires both
  // `:hover` on the row itself AND `.group:hover .just-pulled-marquee`
  // so any pointer entering the parent <section> pauses the animation.
  // The Tailwind arbitrary-property pattern (`group-hover:[…]`) was
  // unreliable across the Tailwind 4 build, so we keep this in plain
  // CSS where the cascade is explicit.
  const style = {
    '--just-pulled-duration': `${durationSeconds}s`,
  } as CSSProperties;

  return (
    <div className="fade-edge-both -mx-[6px] overflow-hidden">
      <div
        className="just-pulled-marquee flex w-max gap-[6px] px-[6px]"
        style={style}
      >
        {items.map((item) => (
          <JustPulledChip
            key={`a-${item.id}`}
            item={item}
            compact={compact}
            {...(onItemClick ? { onClick: onItemClick } : {})}
          />
        ))}
        {items.map((item) => (
          <JustPulledChip
            key={`b-${item.id}`}
            item={item}
            compact={compact}
            {...(onItemClick ? { onClick: onItemClick } : {})}
          />
        ))}
      </div>
    </div>
  );
}

